<template>
    <div class="modal-container">
        <form @submit.prevent="handleSubmit" class="modal-form">
            <div class="modal-header">
                <div class="icon-container">
                    <PhBuildings :size="24" class="text-gray-500" />
                </div>
                <div class="header-text">
                    <h2>{{ isEditing ? 'Edit' : 'Add' }} Department</h2>
                    <p>Departments help organize your employees</p>
                </div>
            </div>

            <div class="modal-body">
                <div class="form-row">
                    <label>Department Name</label>
                    <PrimeInputText
                        v-model="formData.name"
                        placeholder="e.g., Marketing, IT, Human Resources"
                        class="w-full"
                    />
                </div>

                <div class="form-row">
                    <label>Color</label>
                    <div class="color-picker">
                        <div
                            v-for="color in colorOptions"
                            :key="color"
                            class="color-option"
                            :class="{ selected: formData.color === color }"
                            :style="{ backgroundColor: color }"
                            @click="formData.color = color"
                        />
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <PrimeButton label="Cancel" @click="closeDialog" class="p-btn-root_secondary" :disabled="isLoading" />
                <PrimeButton :label="isEditing ? 'Update' : 'Add'" type="submit" :loading="isLoading" />
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { PhBuildings } from '@phosphor-icons/vue';
import { businessService } from '~/services/business-service';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.UPDATE_BUSINESS_DEPARTMENT);
const { showSuccess, showError } = useGlobalToast();
const isLoading = ref(false);

const formData = reactive({
    name: '',
    color: '#3B82F6',
});

const colorOptions = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#06B6D4', // Cyan
    '#84CC16', // Lime
    '#F97316', // Orange
    '#6366F1', // Indigo
];

const companyId = computed(() => dialogRef.value?.data?.companyId);
const department = computed(() => dialogRef.value?.data?.department);
const isEditing = computed(() => !!department.value?.id);

onMounted(() => {
    if (department.value) {
        formData.name = department.value.name || '';
        formData.color = department.value.color || '#3B82F6';
    }
});

const closeDialog = () => {
    dialogRef.value.close();
};

const handleSubmit = async () => {
    if (!formData.name) {
        showError('Error', 'Department name is required');
        return;
    }

    isLoading.value = true;
    try {
        const payload = {
            id: department.value?.id,
            name: formData.name,
            color: formData.color,
        };

        await businessService.saveDepartment(companyId.value, payload);
        showSuccess('Success', `Department ${isEditing.value ? 'updated' : 'added'}`);
        dialogRef.value.close({ updated: true });
    } catch (error: any) {
        showError('Error', error.message || 'Failed to save department');
    } finally {
        isLoading.value = false;
    }
};
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/business-form-modal';

.modal-body {
    .color-picker {
        @apply flex flex-wrap gap-2;

        .color-option {
            @apply w-8 h-8 rounded-full cursor-pointer transition-all;

            &:hover {
                @apply ring-2 ring-offset-2 ring-zinc-300;
            }

            &.selected {
                @apply ring-2 ring-offset-2 ring-zinc-900;
            }
        }
    }
}
</style>
